import { normalizeStripeEnvValue } from "@/utils/stripe/price-env";
import {
  SILVER_MONTHLY_PRICE_ID,
  GOLD_MONTHLY_PRICE_ID,
  DIAMOND_MONTHLY_PRICE_ID,
  DIAMOND_MONTHLY_EXPERIMENT_PRICE_ID,
  SILVER_YEARLY_PRICE_ID,
  GOLD_YEARLY_PRICE_ID,
  DIAMOND_YEARLY_PRICE_ID,
  DIAMOND_PRODUCTION_PRICE_IDS,
  ADDON_EV_SNIPER_MONTHLY_PRICE_ID,
  ADDON_EV_SNIPER_MONTHLY_EXPERIMENT_PRICE_ID,
} from "@/lib/billing/tier-price-ids";

export type ProductKey = "ev-plan" | "whale-watch" | "ev-sniper";

export type SubscriptionLike = {
  price_id: string;
  status?: string | null;
  held_until?: string | null;
  hold_started_at?: string | null;
};

export type ScheduledDowngradeGrantLike = {
  price_id: string;
  kind?: string | null;
  status?: string | null;
  target_takes_effect_at?: string | null;
};

const ACTIVE_STATUSES = new Set(["active", "trialing"]);

function collectKnownPriceIds(values: Array<string | undefined>): string[] {
  const unique = new Set<string>();

  for (const value of values) {
    const normalized = normalizeStripeEnvValue(value);
    if (!normalized) continue;
    unique.add(normalized);
  }

  return Array.from(unique);
}

const EV_PRICE_IDS = collectKnownPriceIds([
  process.env.NEXT_PUBLIC_STRIPE_EV_OPTIMIZER_MONTHLY_PRICE_ID,
  process.env.NEXT_PUBLIC_STRIPE_EV_OPTIMIZER_YEARLY_PRICE_ID,
  process.env.NEXT_PUBLIC_STRIPE_EV_PLAN_MONTHLY_PRICE_ID,
  process.env.NEXT_PUBLIC_STRIPE_EV_PLAN_YEARLY_PRICE_ID,
  process.env.NEXT_PUBLIC_STRIPE_EV_PLAN_MONTHLY_EXPERIMENT_PRICE_ID,
  "price_1SXocCGrgdPJmrXHYzrnVO6G",
  "price_1SXobaGrgdPJmrXHRNiMDPNj",
  "price_1RU9xSGrgdPJmrXHskT7e5i1",
  "price_1RU9yzGrgdPJmrXHjJCU60Ir",
  "price_1T957LGrgdPJmrXH8P3kQfHH", // pragma: allowlist secret
  "price_1T1dyWGrgdPJmrXH0mOE6dXC",
  "price_1SkHXgGrgdPJmrXH1EK1cFqO",
  "price_1ScqLIGrgdPJmrXHSGJenfpZ",
  "price_1T2IJKGrgdPJmrXHPIQ1NKsS",
  SILVER_MONTHLY_PRICE_ID,
  GOLD_MONTHLY_PRICE_ID,
  DIAMOND_MONTHLY_PRICE_ID,
  DIAMOND_MONTHLY_EXPERIMENT_PRICE_ID,
  SILVER_YEARLY_PRICE_ID,
  GOLD_YEARLY_PRICE_ID,
  DIAMOND_YEARLY_PRICE_ID,
  ...DIAMOND_PRODUCTION_PRICE_IDS,
]);

const WHALE_WATCH_PRICE_IDS = collectKnownPriceIds([
  process.env.NEXT_PUBLIC_STRIPE_WHALE_WATCH_MONTHLY_PRICE_ID,
  process.env.NEXT_PUBLIC_STRIPE_WHALE_WATCH_YEARLY_PRICE_ID,
  process.env.NEXT_PUBLIC_STRIPE_WHALE_WATCH_MONTHLY_EXPERIMENT_PRICE_ID,
  "price_1SslQ2GrgdPJmrXHZnffizhY",
  "price_1T6aY1GrgdPJmrXHzJQkoAXK",
  "price_1TDnVdGrgdPJmrXHJO27xhpq", // pragma: allowlist secret
  "price_1SuHHFGrgdPJmrXH8SKddpPd",
  "price_1StuKkGrgdPJmrXHrkWEDDNc",
  "price_1StuCFGrgdPJmrXHsHypVu1S",
  "price_1Sttg5GrgdPJmrXH246fKu3w",
  GOLD_MONTHLY_PRICE_ID,
  DIAMOND_MONTHLY_PRICE_ID,
  DIAMOND_MONTHLY_EXPERIMENT_PRICE_ID,
  GOLD_YEARLY_PRICE_ID,
  DIAMOND_YEARLY_PRICE_ID,
  ...DIAMOND_PRODUCTION_PRICE_IDS,
]);

const EV_SNIPER_PRICE_IDS = collectKnownPriceIds([
  DIAMOND_MONTHLY_PRICE_ID,
  DIAMOND_MONTHLY_EXPERIMENT_PRICE_ID,
  DIAMOND_YEARLY_PRICE_ID,
  ...DIAMOND_PRODUCTION_PRICE_IDS,
  ADDON_EV_SNIPER_MONTHLY_PRICE_ID,
  ADDON_EV_SNIPER_MONTHLY_EXPERIMENT_PRICE_ID,
]);

const LEGACY_EPICK_PRICE_IDS = collectKnownPriceIds([
  process.env.NEXT_PUBLIC_STRIPE_EPICK_TRADER_MONTHLY_PRICE_ID,
  process.env.NEXT_PUBLIC_STRIPE_EPICK_TRADER_YEARLY_PRICE_ID,
  "price_1SIrRrGrgdPJmrXHAMUNjPYw",
  "price_1SIrS7GrgdPJmrXHpnphDe0u",
  "price_1SXRZOGrgdPJmrXHiDzt6Hy2",
]);

export function isSubscriptionOnHold(
  subscription: Pick<SubscriptionLike, "held_until" | "hold_started_at">,
  now: Date = new Date(),
): boolean {
  if (!subscription.held_until) return false;
  const heldUntil = new Date(subscription.held_until);
  if (Number.isNaN(heldUntil.getTime())) return false;
  if (heldUntil.getTime() <= now.getTime()) return false;

  if (subscription.hold_started_at) {
    const holdStartedAt = new Date(subscription.hold_started_at);
    if (!Number.isNaN(holdStartedAt.getTime()) && now.getTime() < holdStartedAt.getTime()) {
      return false;
    }
  }
  return true;
}

export function getGrantedEntitlementsFromPriceIds(priceIds: string[]): Set<ProductKey> {
  const entitlements = new Set<ProductKey>();
  const hasEvPlan = priceIds.some(
    (id) => EV_PRICE_IDS.includes(id) || LEGACY_EPICK_PRICE_IDS.includes(id),
  );
  const hasWhaleWatch = priceIds.some((id) => WHALE_WATCH_PRICE_IDS.includes(id));
  const hasEvSniper = priceIds.some((id) => EV_SNIPER_PRICE_IDS.includes(id));

  if (hasEvPlan) {
    entitlements.add("ev-plan");
  }

  if (hasWhaleWatch) {
    entitlements.add("whale-watch");
  }

  if (hasEvSniper) {
    entitlements.add("ev-sniper");
  }

  return entitlements;
}

function isScheduledDowngradeGrantActive(
  grant: ScheduledDowngradeGrantLike,
  now: Date,
): boolean {
  if (grant.kind != null && grant.kind !== "downgrade") return false;
  if (grant.status != null && grant.status !== "scheduled") return false;
  if (!grant.target_takes_effect_at) return false;
  const targetTakesEffectAt = new Date(grant.target_takes_effect_at);
  if (Number.isNaN(targetTakesEffectAt.getTime())) return false;
  return targetTakesEffectAt.getTime() > now.getTime();
}

export function getScheduledDowngradeEntitlementsFromGrants(
  grants: ScheduledDowngradeGrantLike[],
  now: Date = new Date(),
): Set<ProductKey> {
  const grantPriceIds = grants
    .filter((grant) => isScheduledDowngradeGrantActive(grant, now))
    .map((grant) => grant.price_id);

  return getGrantedEntitlementsFromPriceIds(grantPriceIds);
}

export function getGrantedEntitlementsFromSubscriptions(
  subscriptions: SubscriptionLike[],
  now: Date = new Date(),
  scheduledDowngradeGrants: ScheduledDowngradeGrantLike[] = [],
): Set<ProductKey> {
  const activePriceIds = subscriptions
    .filter((sub) => ACTIVE_STATUSES.has(sub.status ?? "") && !isSubscriptionOnHold(sub, now))
    .map((sub) => sub.price_id);

  const entitlements = getGrantedEntitlementsFromPriceIds(activePriceIds);
  for (const entitlement of getScheduledDowngradeEntitlementsFromGrants(scheduledDowngradeGrants, now)) {
    entitlements.add(entitlement);
  }

  return entitlements;
}

export function getHeldEntitlementsFromSubscriptions(
  subscriptions: SubscriptionLike[],
  now: Date = new Date(),
): Set<ProductKey> {
  const heldPriceIds = subscriptions
    .filter((sub) => ACTIVE_STATUSES.has(sub.status ?? "") && isSubscriptionOnHold(sub, now))
    .map((sub) => sub.price_id);

  return getGrantedEntitlementsFromPriceIds(heldPriceIds);
}

export function hasEntitlement(subscriptions: SubscriptionLike[], entitlement: ProductKey): boolean {
  return getGrantedEntitlementsFromSubscriptions(subscriptions).has(entitlement);
}

export type OnboardingTrack = "diamond" | "gold" | "ev-sniper" | "whale-watch" | "ev-plan";

export function getOnboardingTrack(subscriptions: SubscriptionLike[]): OnboardingTrack | null {
  const entitlements = getGrantedEntitlementsFromSubscriptions(subscriptions);
  const hasEvPlan = entitlements.has("ev-plan");
  const hasWhaleWatch = entitlements.has("whale-watch");
  const hasEvSniper = entitlements.has("ev-sniper");

  if (hasEvPlan && hasWhaleWatch && hasEvSniper) return "diamond";
  if (hasEvPlan && hasWhaleWatch) return "gold";
  if (hasEvSniper) return "ev-sniper";
  if (hasWhaleWatch) return "whale-watch";
  if (hasEvPlan) return "ev-plan";
  return null;
}
